import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './pendingClientRequests.css';

// Field vocabulary shared by the ADD field list and the EDIT diff.
const yesNo = (v) => (v === true || v === 'true' ? 'Yes' : 'No');
const DIFF_FIELDS = [
    { key: 'entity_type', label: 'Type', format: (v) => (v === 'business' ? 'Business' : v === 'individual' ? 'Individual' : '—') },
    { key: 'legal_name', label: 'Business name', format: (v) => (v ? String(v) : '—') },
    { key: 'first_name', label: 'First name', format: (v) => (v ? String(v) : '—') },
    { key: 'last_name', label: 'Last name', format: (v) => (v ? String(v) : '—') },
    { key: 'is_vat_registered', label: 'VAT registered', format: yesNo, bool: true },
    { key: 'trn_number', label: 'TRN number', format: (v) => (v ? String(v) : '—') },
    { key: 'address_line_1', label: 'Address', format: (v) => (v ? String(v) : '—') },
    { key: 'city', label: 'City', format: (v) => (v ? String(v) : '—') },
    { key: 'state', label: 'State / Emirate', format: (v) => (v ? String(v) : '—') },
    { key: 'country', label: 'Country', format: (v) => (v ? String(v) : '—') },
];
const norm = (v) => (v == null ? '' : String(v).trim());

/**
 * Review modal for add_billing_profile AND edit_billing_profile
 * (PLAN_multi_billing.md Round 6).
 *
 * ADD: the payload IS the entity — a simple read-only field list (closest
 * sibling: EditRequestReview's profile view, minus the diff since there is no
 * "original"). Approving creates the Zoho customer.
 *
 * EDIT: the payload is the entity's MERGED requested values and
 * original_snapshot holds the values at submit time — render only the fields
 * that differ, old → new. Approving runs the same shared update path as the
 * staff form (DB + Zoho in one txn).
 */
export default function BillingProfileRequestReview({ request, onClose, onDone, readOnlyOutcome = null }) {
    const isEdit = request.request_type === 'edit_billing_profile';
    // History/list rows can omit original_snapshot — fetch the full request
    // for the edit diff (same pattern as EditRequestReview).
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const rejectReasonRef = useRef(null);

    useEffect(() => {
        if (!isEdit) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/client-change-requests/${request.id}`);
                if (!cancelled) setDetail(res.data?.request || null);
            } catch (e) {
                console.error('Error loading billing entity request detail:', e);
                if (!cancelled) setError('Failed to load the request details.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isEdit, request.id]);

    const p = (detail || request).payload || {};
    const snapshot = (detail || request).original_snapshot || {};
    const isBusiness = p.entity_type === 'business';
    const contactName = [p.first_name, p.last_name].filter(Boolean).join(' ');
    const entityName = isBusiness ? (p.legal_name || '—') : (contactName || '—');
    const address = [p.address_line_1, p.city, p.state, p.country].filter(Boolean).join(', ');

    // EDIT: fields where the merged requested value differs from the snapshot.
    const changedFields = isEdit
        ? DIFF_FIELDS.filter((f) => (f.bool
            ? !!p[f.key] !== !!snapshot[f.key]
            : norm(p[f.key]) !== norm(snapshot[f.key])))
        : [];
    const entityLabel = snapshot.label || p.label || `#${request.target_id}`;

    useEffect(() => {
        if (rejectMode && rejectReasonRef.current) {
            rejectReasonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current.focus();
        }
    }, [rejectMode]);

    const approve = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(`/api/client-change-requests/${request.id}/approve`, {});
            onDone('approved', res.data?.message, res.data?.result_id);
        } catch (err) {
            console.error('Error approving billing entity request:', err);
            setError(err.response?.data?.error || 'Failed to approve request.');
        } finally {
            setSubmitting(false);
        }
    };

    const reject = async () => {
        if (!rejectReason.trim()) {
            setError('A rejection reason is required.');
            rejectReasonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            rejectReasonRef.current?.focus();
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`/api/client-change-requests/${request.id}/reject`, { reason: rejectReason.trim() });
            onDone('rejected');
        } catch (err) {
            console.error('Error rejecting billing entity request:', err);
            setError(err.response?.data?.error || 'Failed to reject request.');
        } finally {
            setSubmitting(false);
        }
    };

    // Never dismiss mid-flight — a failure after unmount would be silent.
    const safeClose = () => { if (!submitting) onClose(); };

    return (
        <div className="pcr-modal-overlay" onClick={safeClose}>
            <div className="pcr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pcr-modal-header">
                    <div>
                        <h3>{isEdit ? 'Review Billing Entity Change' : 'Review New Billing Entity'}</h3>
                        <div className="pcr-modal-sub">
                            {request.client_display_name || request.client_company_name || `Client #${request.client_id}`}
                            {isEdit ? ` · entity “${entityLabel}”` : ''}
                            {' · requested by '}
                            {request.requested_by_first_name} {request.requested_by_last_name}
                        </div>
                    </div>
                    <button className="pcr-modal-close" onClick={safeClose} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="pcr-modal-body">
                    {loading ? (
                        <div className="pcr-empty">Loading request…</div>
                    ) : isEdit ? (
                        <>
                            <div className="pcr-section-title">Requested changes</div>
                            {changedFields.length === 0 ? (
                                <div className="pcr-empty">
                                    No differences from the entity&apos;s current values — the
                                    entity may have been updated since this request was made.
                                </div>
                            ) : (
                                <div className="pcr-diff">
                                    <div className="pcr-diff-row pcr-diff-head">
                                        <span>Field</span>
                                        <span>Original</span>
                                        <span>Requested</span>
                                    </div>
                                    {changedFields.map((f) => (
                                        <div className="pcr-diff-row" key={f.key}>
                                            <span className="pcr-diff-field">{f.label}</span>
                                            <span className="pcr-diff-old">{f.format(snapshot[f.key])}</span>
                                            <span className="pcr-diff-new">{f.format(p[f.key])}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="pcr-hint" style={{ marginTop: 12 }}>
                                Fields not listed stay as they are.
                                {!readOnlyOutcome && ' Approving updates the billing entity and syncs the change to its Zoho customer.'}
                            </div>
                        </>
                    ) : (
                        <>
                    <div className="pcr-section">
                        <div className="pcr-section-title">Requested billing entity</div>
                        <div className="pcr-fields">
                            <div className="pcr-field"><span>Type</span>{isBusiness ? 'Business' : 'Individual'}</div>
                            {isBusiness ? (
                                <>
                                    <div className="pcr-field"><span>Business name</span>{p.legal_name || '—'}</div>
                                    <div className="pcr-field"><span>Contact person</span>{contactName || '—'}</div>
                                </>
                            ) : (
                                <div className="pcr-field"><span>Name</span>{entityName}</div>
                            )}
                            <div className="pcr-field"><span>VAT registered</span>{p.is_vat_registered ? 'Yes' : 'No'}</div>
                            {p.is_vat_registered && (
                                <div className="pcr-field"><span>TRN number</span>{p.trn_number || '—'}</div>
                            )}
                            <div className="pcr-field pcr-field--wide"><span>Billing address</span>{address || '—'}</div>
                        </div>
                    </div>

                    {!readOnlyOutcome && (
                        <div className="pcr-hint" style={{ marginTop: 12 }}>
                            Approving creates the Zoho customer for this entity and lets the
                            client bill properties to it. Invoices for those properties then
                            go to this entity instead of the default one.
                        </div>
                    )}
                        </>
                    )}

                    {rejectMode && (
                        <div className="pcr-section pcr-reason-block pcr-reason-block--reject">
                            <div className="pcr-section-title">Rejection reason (visible to client)</div>
                            <textarea ref={rejectReasonRef} className="pcr-reject-reason" value={rejectReason} maxLength={500} placeholder="Explain why this billing entity is being rejected…" onChange={(e) => setRejectReason(e.target.value)} />
                        </div>
                    )}

                    {!!error && <div className="pcr-error">{error}</div>}
                </div>

                {/* Read-only history view: outcome instead of action buttons. */}
                {readOnlyOutcome ? (
                    <div className="pcr-modal-actions" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{readOnlyOutcome.statusLabel}</strong>
                            {readOnlyOutcome.reviewerName ? <> by <strong>{readOnlyOutcome.reviewerName}</strong></> : null}
                            {readOnlyOutcome.reviewedAt ? <> · {readOnlyOutcome.reviewedAt}</> : null}
                            {readOnlyOutcome.reason ? <div><strong>Reason:</strong> {readOnlyOutcome.reason}</div> : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {readOnlyOutcome.onOpenEntity ? (
                                <button className="pcr-btn pcr-btn--primary" onClick={readOnlyOutcome.onOpenEntity}>
                                    {readOnlyOutcome.entityLabel}
                                </button>
                            ) : null}
                            <button className="pcr-btn pcr-btn--ghost" onClick={onClose}>Close</button>
                        </div>
                    </div>
                ) : (
                <div className="pcr-modal-actions">
                    {rejectMode ? (
                        <>
                            <button className="pcr-btn pcr-btn--ghost" onClick={() => { setRejectMode(false); setError(''); }} disabled={submitting}>Back</button>
                            <button className="pcr-btn pcr-btn--danger" onClick={reject} disabled={submitting}>{submitting ? 'Rejecting…' : 'Confirm Reject'}</button>
                        </>
                    ) : (
                        <>
                            <button className="pcr-btn pcr-btn--ghost pcr-btn--danger-text" onClick={() => { setRejectMode(true); setError(''); }} disabled={submitting || loading}>Reject</button>
                            <button className="pcr-btn pcr-btn--primary" onClick={approve} disabled={submitting || loading}>
                                {submitting ? 'Approving…' : isEdit ? 'Approve & Update Entity' : 'Approve & Create Entity'}
                            </button>
                        </>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
